import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPoll,
    faArrowLeft,
    faArrowRight,
    faInfoCircle,
    faCheckCircle,
    faQrcode,
    faFilePdf,
    faLink,
    faPowerOff,
    faPlay
} from '@fortawesome/free-solid-svg-icons';
import DocumentationLayout from '../../../components/documentation/DocumentationLayout';

function FeedbackEncuestas() {
    return (
        <DocumentationLayout>
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-4xl font-bold text-gray-800 mb-4">
                        Encuestas de Satisfacción
                    </h1>
                    <p className="text-xl text-gray-600">
                        Crea y gestiona encuestas para recopilar feedback valioso de tus clientes y mejorar tu servicio.
                    </p>
                </div>

                {/* Acceso */}
                <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-3 flex items-center">
                        <FontAwesomeIcon icon={faInfoCircle} className="mr-2 text-blue-600" />
                        ¿Cómo acceder?
                    </h2>
                    <p className="text-gray-700 mb-2">
                        Para acceder al Dashboard de Encuestas:
                    </p>
                    <ol className="list-decimal list-inside text-gray-700 space-y-2 ml-4">
                        <li>Inicia sesión en tu cuenta de PetitPrime</li>
                        <li>En el menú principal, busca la sección <strong>"Feedback"</strong> o <strong>"Encuestas"</strong></li>
                        <li>Haz clic en <strong>"Dashboard de Encuestas"</strong></li>
                        <li>O accede directamente a: <code className="bg-white px-2 py-1 rounded">/dashboard-surveys</code></li>
                    </ol>
                </div>

                {/* Crear Encuesta */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        1. Crear una Nueva Encuesta
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            En la parte superior del dashboard encontrarás un botón <strong>"Crear encuesta nueva"</strong>.
                        </p>
                        
                        <div className="bg-gray-50 p-4 rounded-lg mb-4">
                            <h3 className="text-lg font-semibold text-gray-800 mb-3">
                                Proceso de Creación
                            </h3>
                            <ol className="list-decimal list-inside text-gray-700 space-y-2 ml-4">
                                <li>Haz clic en el botón <strong>"Crear encuesta nueva"</strong></li>
                                <li>Se abrirá un modal donde deberás ingresar el <strong>título de la encuesta</strong></li>
                                <li>Escribe un título descriptivo, por ejemplo: "Encuesta de Satisfacción - Enero 2024"</li>
                                <li>Haz clic en <strong>"Crear Encuesta"</strong></li>
                                <li>La encuesta se creará automáticamente con preguntas predefinidas de satisfacción</li>
                            </ol>
                        </div>

                        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded mb-4">
                            <p className="text-sm text-gray-700">
                                <strong>💡 Tip:</strong> Las encuestas se crean con un conjunto de preguntas predefinidas que incluyen evaluación de calidad, satisfacción, tiempo de espera, ambiente, precio y probabilidad de recomendación (NPS). También incluyen preguntas abiertas para comentarios adicionales.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Gestión de Encuestas */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        2. Gestionar tus Encuestas
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            Una vez creadas, tus encuestas aparecerán en tarjetas en el dashboard. Cada tarjeta muestra el título de la encuesta y varios botones de acción:
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faQrcode} className="mr-2 text-blue-600" />
                                    Ver Código QR
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Muestra un código QR que los clientes pueden escanear con su teléfono para acceder directamente a la encuesta. Ideal para imprimir y colocar en tu tienda.
                                </p>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faFilePdf} className="mr-2 text-red-600" />
                                    Descargar PDF
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Genera y descarga un PDF profesional con el código QR incluido. El PDF incluye instrucciones para los clientes y puede imprimirse para colocarlo en tu tienda.
                                </p>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faLink} className="mr-2 text-green-600" />
                                    Ver Enlace
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Muestra el enlace directo de la encuesta que puedes copiar y compartir por WhatsApp, email, redes sociales o cualquier otro canal de comunicación.
                                </p>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faPoll} className="mr-2 text-yellow-600" />
                                    Ver Resultados
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Accede a un análisis detallado de todas las respuestas recibidas, incluyendo estadísticas, gráficos y comentarios de los clientes.
                                </p>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faPowerOff} className="mr-2 text-purple-600" />
                                    Activar/Desactivar
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Activa o desactiva la encuesta. Cuando está desactivada, los clientes no pueden responder, pero puedes seguir viendo los resultados históricos.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Compartir Encuestas */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        3. Compartir Encuestas con tus Clientes
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            Hay varias formas de compartir tus encuestas para maximizar la participación:
                        </p>

                        <div className="space-y-4">
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-800 mb-2">📱 Código QR</h3>
                                <p className="text-sm text-gray-700 mb-2">
                                    <strong>Mejor para:</strong> Clientes presenciales en tu tienda
                                </p>
                                <ol className="list-decimal list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li>Haz clic en el botón de código QR de tu encuesta</li>
                                    <li>Imprime el código QR o descarga el PDF</li>
                                    <li>Colócalo en un lugar visible de tu tienda (mostrador, mesas, entrada)</li>
                                    <li>Los clientes escanean con su teléfono y responden inmediatamente</li>
                                </ol>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-800 mb-2">🔗 Enlace Directo</h3>
                                <p className="text-sm text-gray-700 mb-2">
                                    <strong>Mejor para:</strong> Compartir por WhatsApp, email o redes sociales
                                </p>
                                <ol className="list-decimal list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li>Haz clic en el botón de enlace de tu encuesta</li>
                                    <li>Copia el enlace que aparece en el modal</li>
                                    <li>Compártelo por WhatsApp, email, redes sociales o cualquier canal</li>
                                    <li>Los clientes hacen clic y responden directamente</li>
                                </ol>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-800 mb-2">📄 PDF con QR</h3>
                                <p className="text-sm text-gray-700 mb-2">
                                    <strong>Mejor para:</strong> Imprimir y distribuir físicamente
                                </p>
                                <ol className="list-decimal list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li>Haz clic en el botón de descargar PDF</li>
                                    <li>El PDF se descargará automáticamente con el código QR incluido</li>
                                    <li>Imprime el PDF y colócalo en tu tienda o inclúyelo en entregas</li>
                                    <li>El PDF incluye instrucciones claras para los clientes</li>
                                </ol>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Ver Resultados */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        4. Ver y Analizar Resultados
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            Para ver los resultados de tus encuestas:
                        </p>
                        <ol className="list-decimal list-inside text-gray-700 space-y-2 ml-4 mb-4">
                            <li>Haz clic en el botón de resultados (icono de gráfico) en la tarjeta de la encuesta</li>
                            <li>Se abrirá una página con el análisis completo de las respuestas</li>
                            <li>Verás estadísticas de cada pregunta, gráficos de distribución de respuestas y comentarios abiertos</li>
                        </ol>

                        <div className="bg-gray-50 p-4 rounded-lg">
                            <h3 className="font-semibold text-gray-800 mb-2">Información Disponible en los Resultados:</h3>
                            <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                <li><strong>Preguntas Likert:</strong> Distribución de respuestas del 1 al 5 (o 1 al 10 para NPS) con porcentajes</li>
                                <li><strong>Preguntas Abiertas:</strong> Todos los comentarios y sugerencias de los clientes</li>
                                <li><strong>Estadísticas:</strong> Promedios, totales y tendencias</li>
                                <li><strong>Visualizaciones:</strong> Gráficos que facilitan la interpretación de los datos</li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Mejores Prácticas */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        5. Mejores Prácticas
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <ul className="space-y-4">
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Coloca códigos QR en lugares estratégicos:</strong> En el mostrador, mesas, entrada y baños para maximizar la visibilidad.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Menciona las encuestas a tus clientes:</strong> Invítalos amablemente a compartir su opinión.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Revisa los resultados regularmente:</strong> Analiza el feedback para identificar áreas de mejora y tomar acciones.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Actúa sobre el feedback:</strong> Cuando recibas comentarios negativos, toma medidas para mejorar. Cuando recibas comentarios positivos, agradece y continúa con esas prácticas.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Crea encuestas periódicas:</strong> Establece un calendario (mensual, trimestral) para medir la evolución de la satisfacción.
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Troubleshooting */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        6. Solución de Problemas
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="space-y-4">
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">El código QR no se genera</h4>
                                <p className="text-gray-700 text-sm">
                                    Asegúrate de tener conexión a internet. El código QR se genera en tiempo real desde el enlace de la encuesta. Si el problema persiste, intenta refrescar la página.
                                </p>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">No puedo descargar el PDF</h4>
                                <p className="text-gray-700 text-sm">
                                    Verifica que tu navegador permita descargas. Algunos navegadores bloquean descargas automáticas. Intenta permitir descargas en la configuración del navegador.
                                </p>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">Los clientes no pueden acceder a la encuesta</h4>
                                <p className="text-gray-700 text-sm">
                                    Verifica que la encuesta esté activada (el botón debe mostrar el icono de "power off" en color púrpura). Si está desactivada, los clientes no podrán responder.
                                </p>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">No veo resultados en mi encuesta</h4>
                                <p className="text-gray-700 text-sm">
                                    Es normal si acabas de crear la encuesta y aún no has recibido respuestas. Comparte la encuesta con tus clientes usando los métodos descritos arriba para empezar a recibir feedback.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Navegación */}
                <div className="flex justify-between items-center pt-8 border-t">
                    <Link
                        to="/documentation/feedback"
                        className="inline-flex items-center text-purple-600 hover:text-purple-800 font-medium"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                        Volver a Feedback y Alertas
                    </Link>
                    <Link
                        to="/documentation/feedback/alertas"
                        className="inline-flex items-center text-purple-600 hover:text-purple-800 font-medium"
                    >
                        Siguiente: Alertas
                        <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                    </Link>
                </div>
            </div>
        </DocumentationLayout>
    );
}

export default FeedbackEncuestas;

